import { useState } from 'react';
import { useStore } from '../store';
import { useT } from '../utils/i18n';

interface InviteModalProps {
  onClose: () => void;
}

export function InviteModal({ onClose }: InviteModalProps) {
  const t = useT();
  const token = useStore((s) => s.token);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async () => {
    if (!email.trim() || !token) return;
    setSending(true);
    setError('');
    try {
      const res = await fetch('/auth/workspace/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to send invite');
        setSending(false);
        return;
      }
      setSent(true);
      setTimeout(onClose, 1500);
    } catch {
      setError('Network error');
      setSending(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.6)', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: '#2a2a22', borderRadius: 16, padding: '28px 24px',
        maxWidth: 380, width: '90%',
        border: '1px solid rgba(255,255,255,0.12)',
      }} onClick={(e) => e.stopPropagation()}>
        {sent ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
            <p style={{ color: '#f1ecdf', fontSize: 15, fontWeight: 600 }}>{t('team.sent')}</p>
            <p style={{ color: '#9E9E8E', fontSize: 13, marginTop: 6 }}>{email}</p>
          </div>
        ) : (
          <>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: '#f1ecdf', marginBottom: 4 }}>
              {t('team.inviteTitle')}
            </h3>
            <p style={{ fontSize: 12, color: '#9E9E8E', marginBottom: 20, lineHeight: 1.5 }}>
              {t('team.inviteDesc')}
            </p>

            <label style={{ fontSize: 12, fontWeight: 600, color: '#b0a890', display: 'block', marginBottom: 6 }}>
              {t('team.email')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('team.emailPlaceholder')}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
              autoFocus
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.15)', background: '#1b1b14',
                color: '#f1ecdf', fontSize: 14, outline: 'none', marginBottom: 14,
                boxSizing: 'border-box',
              }}
            />

            {error && (
              <p style={{ fontSize: 12, color: '#e34a2a', marginBottom: 12 }}>{error}</p>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{
                padding: '9px 20px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)',
                background: 'transparent', color: '#f1ecdf', cursor: 'pointer', fontSize: 13, fontWeight: 500,
              }}>{t('team.cancel')}</button>
              <button
                onClick={handleSend}
                disabled={sending || !email.trim()}
                style={{
                  padding: '9px 20px', borderRadius: 8, border: 'none',
                  background: '#5a6a2a', color: '#f1ecdf', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600,
                  opacity: sending || !email.trim() ? 0.5 : 1,
                }}
              >
                {sending ? t('team.sending') : t('team.send')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
